import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PageHeader from "../../component/pageHeaders";
import DataTable from "./components/datatable";
import { API } from "../../config/API";
import Loadingbox from "../../component/loadingbox";
import api from "../../config/axiosInstance";
import { Input, Tag, Button, Select, message, notification } from "antd";
import * as XLSX from "xlsx";
// import { IoMdCloseCircle } from "react-icons/io5";
import { GET, PUT } from "../../utils/apirequest";
import {
  ZONE_CODE_MAP,
  DISTRICT_CODE_MAP,
  zones,
  districts,
} from "../../config/zones";
import { useEpaperConfig } from "../../config/epaperConfig";

function AgentsScreen() {
  // Zone filter options come from E-Paper Settings.
  useEpaperConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [indicator, setIndicator] = useState<boolean>(true);
  const [page, setPage] = useState(Number(query?.get("page")) || 1);
  const [pageSize, setPageSize] = useState(Number(query?.get("limit")) || 10);
  const [meta, setMeta] = useState() as any;
  const [searchTerm, setSearchTerm] = useState("");
  const [zoneFilter, setZoneFilter] = useState<string | undefined>(undefined);
  const [districtFilter, setDistrictFilter] = useState<string | undefined>(
    undefined
  );
  const [blockingId, setBlockingId] = useState<number | null>(null);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setPage(Number(query?.get("page")) || 1);
    setPageSize(Number(query?.get("limit")) || 10);
  }, [location.search]);

  const loadData = async (
    search?: string,
    pg?: any,
    ps?: any,
    zoneOverride?: string,
    districtOverride?: string
  ) => {
    setIndicator(true);
    try {
      const currentPage = pg || page || 1;
      const currentPageSize = ps || pageSize || 10;
      const activeZone = zoneOverride !== undefined ? zoneOverride : zoneFilter;
      const activeDistrict =
        districtOverride !== undefined ? districtOverride : districtFilter;
      const params = new URLSearchParams({
        page: currentPage.toString(),
        pageSize: currentPageSize.toString(),
        ...(search && { search }),
        ...(activeZone && { zone: activeZone }),
        ...(activeDistrict && { district: activeDistrict }),
      });

      const res: any = await GET(
        `${API.EPAPER_AGENTS_LIST}?${params.toString()}`,
        null,
        null
      );

      if (res && res.data) {
        setData(res.data);
        setMeta(res.pagination?.total);
        setIsLoading(false);
        setIndicator(false);
      } else {
        setIsLoading(false);
        setIndicator(false);
      }
    } catch (err) {
      setIsLoading(false);
      setIndicator(false);
      console.log("err", err);
      message.error("Something went wrong...");
    }
  };

  const Search = async (searchText: string) => {
    setSearchTerm(searchText);
    if (searchText.length > 2) {
      setTimeout(() => {
        loadData(searchText, 1, pageSize);
      }, 500);
    } else if (searchText.length === 0) {
      loadData("", 1, pageSize);
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
    loadData("", 1, pageSize);
  };

  const onZoneChange = (val?: string) => {
    setZoneFilter(val);
    loadData(searchTerm, 1, pageSize, val ?? "", districtFilter ?? "");
  };

  const onDistrictChange = (val?: string) => {
    setDistrictFilter(val);
    loadData(searchTerm, 1, pageSize, zoneFilter ?? "", val ?? "");
  };

  const clearZone = () => onZoneChange(undefined);
  const clearDistrict = () => onDistrictChange(undefined);

  const exportAgents = async () => {
    setExportLoading(true);
    try {
      const params = new URLSearchParams({
        ...(searchTerm && { search: searchTerm }),
        ...(zoneFilter && { zone: zoneFilter }),
        ...(districtFilter && { district: districtFilter }),
      });

      const response: any = await GET(
        `${API.EPAPER_AGENTS_EXPORT}?${params.toString()}`,
        null,
        null
      );

      if (response && response.data) {
        const ws = XLSX.utils.json_to_sheet(response.data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Agents");
        const filename =
          response.filename ||
          `agents-${new Date().toISOString().split("T")[0]}`;
        XLSX.writeFile(wb, `${filename}.xlsx`);
        message.success(`Exported ${response.total} agents to Excel`);
      } else {
        message.error("No data to export");
      }
    } catch (err) {
      console.error("Export agents error:", err);
      message.error("Failed to export agents");
    } finally {
      setExportLoading(false);
    }
  };

  const onPageChange = (pg: any, ps: any) => {
    query.set("page", pg);
    query.set("limit", ps);
    navigate({ search: query.toString() });
    window.scrollTo(0, 0);
    setPage(pg);
    setPageSize(ps);
    loadData(searchTerm, pg, ps);
  };

  const deleteData = async (id: any) => {
    setIsLoading(true);
    try {
      await api.delete(API.DELETE_USER + `${id}`);
      message.success("Agent deleted and all sessions revoked");
      loadData(searchTerm);
    } catch (err) {
      console.log(err);
      message.error("Something went wrong...!");
      setIsLoading(false);
    }
  };

  const updateData = async (id: any, blocked: boolean) => {
    setBlockingId(id);
    try {
      if (blocked) {
        await api.put(API.AUTH_ADMIN_BLOCK_USER + `${id}`);
        notification.success({
          message: "Agent Blocked",
          description: "Agent has been blocked and all active sessions have been revoked.",
        });
      } else {
        await api.put(API.AUTH_ADMIN_UNBLOCK_USER + `${id}`);
        notification.success({
          message: "Agent Activated",
          description: "Agent has been unblocked and can now log in.",
        });
      }
      loadData(searchTerm);
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Couldn't complete updating agent.",
      });
      console.error("An error occurred:", error);
    } finally {
      setBlockingId(null);
    }
  };

  return (
    <div>
      <PageHeader
        title={"Agents"}
        loader={indicator}
        length={`Dashboard / Agents`}
      >
        <div className="homescreen-pageItem">
          <Input
            style={{ width: "250px" }}
            allowClear
            placeholder="Search by name, email, code, zone or district"
            value={searchTerm}
            onChange={(e: any) => Search(e.target.value)}
          />
          <div style={{ width: 10 }} />
          <Select
            style={{ width: 180 }}
            allowClear
            showSearch
            placeholder="Filter by Zone"
            value={zoneFilter}
            onChange={onZoneChange}
            optionFilterProp="children"
          >
            {zones.map((z) => (
              <Select.Option key={z} value={z}>
                {z} ({ZONE_CODE_MAP[z]})
              </Select.Option>
            ))}
          </Select>
          <div style={{ width: 10 }} />
          <Select
            style={{ width: 200 }}
            allowClear
            showSearch
            placeholder="Filter by District"
            value={districtFilter}
            onChange={onDistrictChange}
            optionFilterProp="children"
          >
            {districts.map((d) => (
              <Select.Option key={d} value={d}>
                {d} ({DISTRICT_CODE_MAP[d]})
              </Select.Option>
            ))}
          </Select>
          <div style={{ width: 10 }} />
          <Button
            onClick={exportAgents}
            loading={exportLoading}
          >
            Export
          </Button>
          <div style={{ width: 10 }} />
          <Button
            type="primary"
            onClick={() => navigate("/Auth/agents/form/create")}
          >
            Create Agent
          </Button>
        </div>
      </PageHeader>
      <div style={{ padding: 5 }} />
      <div className="PageHeaders-box5">
        <div className="PageHeaders-txt3">Agents ( {meta} )</div>
        {searchTerm || zoneFilter || districtFilter ? (
          <>
            <div>|</div>
            <div className="PageHeaders-txt2">Filter By : </div>
            {searchTerm ? (
              <Tag onClick={clearSearch}>
                &nbsp;search : {searchTerm}
              </Tag>
            ) : null}
            {zoneFilter ? (
              <Tag onClick={clearZone}>&nbsp;zone : {zoneFilter}</Tag>
            ) : null}
            {districtFilter ? (
              <Tag onClick={clearDistrict}>
                &nbsp;district : {districtFilter}
              </Tag>
            ) : null}
          </>
        ) : null}
      </div>
      {isLoading ? (
        <Loadingbox />
      ) : (
        <DataTable
          data={data}
          meta={meta}
          page={page}
          pagesize={pageSize}
          onDelete={(id: any) => deleteData(id)}
          onUpdate={(id: any, blocked: boolean) => updateData(id, blocked)}
          blockingId={blockingId}
          onPageChange={(pg: any, ps: any) => onPageChange(pg, ps)}
        />
      )}
    </div>
  );
}

export default AgentsScreen;
